import { useEffect, useMemo, useRef, useState } from 'react';

const ALTEZZA_ORA = 48;          // px
const GIORNI_BREVI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const chiave = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const minuti = d => d.getHours() * 60 + d.getMinutes();
const inizioGiorno = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Eventi che si sovrappongono: si dividono la larghezza, come su un calendario vero. */
function disponi(eventi) {
  const ordinati = [...eventi].sort((a, b) => a.da - b.da || b.a - a.a);
  const colonne = [];
  for (const evento of ordinati) {
    let indice = colonne.findIndex(colonna => colonna[colonna.length - 1].a <= evento.da);
    if (indice === -1) { colonne.push([evento]); indice = colonne.length - 1; }
    else colonne[indice].push(evento);
    evento.colonna = indice;
  }
  // Il numero di colonne vale per l'intero gruppo sovrapposto: larghezza uguale per tutti.
  const totale = Math.max(1, colonne.length);
  ordinati.forEach(e => { e.colonne = totale; });
  return ordinati;
}

export default function WeekGrid({ days, events, onSlotClick, onEventClick }) {
  const contenitore = useRef(null);
  const [adesso, setAdesso] = useState(() => new Date());
  const primoGiorno = days[0]?.getTime();

  useEffect(() => {
    const t = setInterval(() => setAdesso(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // Si parte dalle 8:00: le ore piccole non interessano quasi mai.
    if (contenitore.current) contenitore.current.scrollTop = 8 * ALTEZZA_ORA;
  }, [primoGiorno]);

  const { tuttoIlGiorno, perGiorno } = useMemo(() => {
    const tutto = [];
    const mappa = Object.fromEntries(days.map(g => [chiave(g), []]));
    for (const evento of events) {
      const da = new Date(evento.start);
      const a = new Date(evento.end || evento.start);
      if (evento.all_day || a - da >= 24 * 3600 * 1000) { tutto.push({ ...evento, da, a }); continue; }
      const k = chiave(da);
      if (mappa[k]) mappa[k].push({ ...evento, da, a, minDa: minuti(da), minA: Math.max(minuti(da) + 15, minuti(a)) });
    }
    Object.keys(mappa).forEach(k => {
      mappa[k] = disponi(mappa[k].map(e => ({ ...e, da: e.minDa, a: e.minA })));
    });
    return { tuttoIlGiorno: tutto, perGiorno: mappa };
  }, [days, events]);

  const oggi = chiave(new Date());
  const colonnaOggi = days.findIndex(g => chiave(g) === oggi);

  return (
    <div className="overflow-hidden rounded-lg border border-line bg-surface">
      <div className="grid border-b border-line" style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, 1fr)` }}>
        <div />
        {days.map(giorno => (
          <div key={chiave(giorno)} className={`px-1 py-2 text-center ${chiave(giorno) === oggi ? 'bg-brand-50' : ''}`}>
            <p className="text-2xs uppercase text-muted">{GIORNI_BREVI[(giorno.getDay() + 6) % 7]}</p>
            <p className={`text-sm tabular-nums ${chiave(giorno) === oggi ? 'font-semibold text-brand-700' : 'text-ink'}`}>
              {giorno.getDate()}
            </p>
          </div>
        ))}
      </div>

      {tuttoIlGiorno.length > 0 && (
        <div className="grid border-b border-line bg-surface-2" style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, 1fr)` }}>
          <div className="px-1 py-1 text-right text-2xs text-muted">tutto il giorno</div>
          <div className="col-span-full col-start-2 space-y-1 p-1">
            {tuttoIlGiorno.map((evento, i) => {
              const primo = Math.max(0, days.findIndex(g => inizioGiorno(g) >= inizioGiorno(evento.da)));
              const ultimo = days.reduce((acc, g, idx) => (inizioGiorno(g) < evento.a ? idx : acc), primo);
              return (
                <button key={`${evento.event_id}-${i}`} type="button" onClick={() => onEventClick(evento)}
                  title={`${evento.title}${evento.location ? ` — ${evento.location}` : ''}`}
                  className="block w-full truncate rounded px-2 py-0.5 text-left text-xs text-white"
                  style={{
                    backgroundColor: evento.color || '#16a34a',
                    marginLeft: `${(primo / days.length) * 100}%`,
                    width: `${((ultimo - primo + 1) / days.length) * 100}%`,
                  }}>
                  {evento.title}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div ref={contenitore} className="max-h-[60vh] overflow-y-auto">
        <div className="relative grid" style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, 1fr)` }}>
          <div>
            {Array.from({ length: 24 }, (_, ora) => (
              <div key={ora} className="relative border-b border-line/60" style={{ height: ALTEZZA_ORA }}>
                <span className="absolute -top-2 right-1 text-2xs tabular-nums text-muted">
                  {ora > 0 ? `${String(ora).padStart(2, '0')}:00` : ''}
                </span>
              </div>
            ))}
          </div>

          {days.map(giorno => (
            <div key={chiave(giorno)} className="relative border-l border-line">
              {Array.from({ length: 24 }, (_, ora) => (
                <button key={ora} type="button" aria-label={`Nuovo evento il ${giorno.toLocaleDateString('it-IT')} alle ${ora}:00`}
                  onClick={() => onSlotClick(new Date(giorno.getFullYear(), giorno.getMonth(), giorno.getDate(), ora))}
                  className="block w-full border-b border-line/60 hover:bg-brand-50/50"
                  style={{ height: ALTEZZA_ORA }} />
              ))}

              {(perGiorno[chiave(giorno)] || []).map((evento, i) => (
                <button key={`${evento.event_id}-${i}`} type="button" onClick={() => onEventClick(evento)}
                  className="absolute overflow-hidden rounded px-1 py-0.5 text-left text-2xs leading-tight text-white shadow-card"
                  style={{
                    top: (evento.da / 60) * ALTEZZA_ORA,
                    height: Math.max(18, ((evento.a - evento.da) / 60) * ALTEZZA_ORA - 2),
                    left: `${(evento.colonna / evento.colonne) * 100}%`,
                    width: `calc(${(1 / evento.colonne) * 100}% - 2px)`,
                    backgroundColor: evento.color || '#16a34a',
                  }}
                  title={[
                    evento.title,
                    `${new Date(evento.start).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
                      + `–${new Date(evento.end || evento.start).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`,
                    evento.location,
                  ].filter(Boolean).join(' — ')}>
                  <span className="block truncate font-medium">{evento.title}</span>
                  <span className="block truncate opacity-90">
                    {new Date(evento.start).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </button>
              ))}
            </div>
          ))}

          {colonnaOggi >= 0 && (
            <div aria-hidden className="pointer-events-none absolute left-14 right-0 z-10"
              style={{ top: (minuti(adesso) / 60) * ALTEZZA_ORA }}>
              <div className="h-px bg-danger" />
              <div className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-danger" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
